import {Box, Typography} from '@mui/material';
import React, {Fragment, useCallback} from 'react';
import {useNavigate, Link} from 'react-router-dom';

import {MainFooter, MainHeader, SubheaderWithSearch} from 'presentation/components';
import {Filter} from 'presentation/components/Filter';
import {GuidesEnum, QueryParamsEnum} from 'presentation/constants';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {formatText, getInitialPath, getPageHeaderTitle, getUfPath} from 'presentation/utils';
import {CityList} from './components/CityList';
import {CitySelectionProps} from './types';
import {FlowEnum, PATH_TITLE} from '../booking/constants';
import {StyledBreadcrumbs} from '../uf-selection/styles';
import {GuideStep} from 'presentation/modules/guide/shared/constants/constants';

const {city, locality} = translations['pt-br'].privateFlow.profile.locationSelection;
const {booking} = translations['pt-br'].others.sidebar;
const {city: cityTitle, state: stateTitle, selectCity} = translations['pt-br'].commons;

export function CitySelection({flowType}: CitySelectionProps) {
	const navigate = useNavigate();
	const {SEARCH_PARAMS} = useQueryParams();
	const {ufCode, ufName} = SEARCH_PARAMS;
	const isOdontoFlow = flowType === FlowEnum.ODONTO;
	const isGuideFlow = flowType === FlowEnum.HEALTH_GUIDE || flowType === FlowEnum.ODONTO_GUIDE;
	const showBreadcrumbs =
		flowType === FlowEnum.HEALTH_GUIDE || flowType === FlowEnum.ODONTO_GUIDE || isOdontoFlow;
	const guideFlowPath =
		flowType === FlowEnum.ODONTO_GUIDE
			? GuidesEnum.SERVICE_NETWORK_ODONTO_FLOW_UF
			: GuidesEnum.SERVICE_NETWORK_HEALTH_FLOW_UF;

	const pageTitle = getPageHeaderTitle({internalPaths: PATH_TITLE, mainPath: booking});
	const initialPath = getInitialPath(flowType);
	const bookingUfPath = getUfPath(flowType);
	const subheaderTitle = `${city.title} ${formatText(ufName)}`;

	const handleClose = useCallback(() => {
		if (isGuideFlow) {
			const guidePath =
				flowType === FlowEnum.ODONTO_GUIDE
					? GuidesEnum.SERVICE_NETWORK_ODONTO
					: GuidesEnum.SERVICE_NETWORK_HEALTH;
			navigate(`${guidePath}?${QueryParamsEnum.STEP}=${GuideStep.LOCATION}`);
			return;
		}

		if (isOdontoFlow) {
			navigate(bookingUfPath);
			return;
		}

		navigate(initialPath);
	}, [initialPath, isGuideFlow]);

	const handleBack = useCallback(() => {
		if (isOdontoFlow) {
			navigate(bookingUfPath);
			return;
		}

		navigate(
			`${bookingUfPath}?${QueryParamsEnum.UF_CODE}=${ufCode}&${QueryParamsEnum.UF_NAME}=${ufName}`,
		);
	}, [bookingUfPath, ufCode, ufName]);

	return (
		<Fragment>
			<MainHeader
				title={isGuideFlow ? selectCity : pageTitle}
				handleBack={handleBack}
				handleClose={handleClose}
				variant="subRoute">
				{showBreadcrumbs ? (
					<StyledBreadcrumbs>
						<Link to={isGuideFlow ? guideFlowPath : bookingUfPath}>
							<Typography
								variant="body1"
								fontWeight={600}
								color="white"
								sx={{textDecoration: 'underline'}}>
								{stateTitle}
							</Typography>
						</Link>
						<Typography variant="body1" color="white">
							{cityTitle}
						</Typography>
					</StyledBreadcrumbs>
				) : (
					<SubheaderWithSearch title={subheaderTitle} placeholder={city.searchCities} />
				)}
				<Filter.Root>
					<Filter.Search
						placeholder={isOdontoFlow ? locality.searchRegion : city.searchCities}
					/>
				</Filter.Root>
			</MainHeader>

			<CityList flowType={flowType} />

			<Box mt="auto">
				<MainFooter />
			</Box>
		</Fragment>
	);
}
